import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/dashboard/PageHeader';

interface SwapForm {
  againstListingId: string;
  myDeviceTitle: string;
  myDeviceImei: string;
  myDeviceCondition: string;
  myDeviceValueGBP: number;
  topUpGBP?: number;
  notes?: string;
}

export default function SwapDevice() {
  const [params] = useSearchParams();
  const { register, handleSubmit, watch, formState: { isSubmitting } } = useForm<SwapForm>({
    defaultValues: { againstListingId: params.get('against') ?? '', myDeviceCondition: 'EXCELLENT' },
  });

  const myValue = watch('myDeviceValueGBP') || 0;
  const topUp = watch('topUpGBP') || 0;

  async function onSubmit(values: SwapForm) {
    try {
      await api.post('/swaps', values);
      toast.success('Swap proposal sent - seller has 48h to respond.');
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Could not send');
    }
  }

  return (
    <div>
      <PageHeader
        eyebrow="DEVICE EXCHANGE"
        title="Propose a"
        accent="swap."
        subtitle="Both items are held securely and partner lockers. Souqli verifies both serials before either party releases funds."
      />

      {/* Visual exchange diagram */}
      <div className="mt-8 card p-6 bg-cream-deep">
        <div className="grid grid-cols-3 gap-2 sm:gap-4 items-center">
          <div className="text-center">
            <div className="stamp text-gold-dark mb-2">Your device</div>
            <div className="display text-2xl">£{myValue.toFixed(0)}</div>
            <div className="text-xs text-mute mt-1">+ £{topUp.toFixed(0)} top-up</div>
          </div>
          <div className="flex justify-center">
            <svg viewBox="0 0 100 40" className="w-full max-w-[140px] text-gold">
              <path d="M5 12 H85 M85 12 L75 4 M85 12 L75 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              <path d="M95 28 H15 M15 28 L25 20 M15 28 L25 36" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>
          <div className="text-center">
            <div className="stamp text-gold-dark mb-2">Their device</div>
            <div className="display text-2xl">£?</div>
            <div className="text-xs text-mute mt-1">From listing</div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="card p-6 mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="col-span-2">
          <label className="label">Listing ID you want</label>
          <input className="input input-lg mono" placeholder="e.g. ckl9z…" {...register('againstListingId', { required: true })} />
        </div>

        <div className="col-span-2 mt-2">
          <div className="stamp text-gold-dark mb-3">Your device</div>
        </div>
        <div className="col-span-2">
          <label className="label">Title</label>
          <input className="input input-lg" placeholder="iPhone 14 Pro 256GB Deep Purple" {...register('myDeviceTitle', { required: true })} />
        </div>
        <div>
          <label className="label">IMEI / serial</label>
          <input className="input input-lg mono" {...register('myDeviceImei', { required: true, minLength: 8 })} />
          <p className="stamp text-mute mt-1.5">⚠ Required - checked against stolen-goods register.</p>
        </div>
        <div>
          <label className="label">Condition</label>
          <select className="input input-lg" {...register('myDeviceCondition', { required: true })}>
            {['LIKE_NEW','EXCELLENT','GOOD','FAIR'].map((c) => <option key={c} value={c}>{c.replace('_',' ').toLowerCase()}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Estimated value (£)</label>
          <input className="input input-lg" type="number" min={1} {...register('myDeviceValueGBP', { required: true, valueAsNumber: true })} />
        </div>
        <div>
          <label className="label">Top-up cash (optional, £)</label>
          <input className="input input-lg" type="number" min={0} {...register('topUpGBP', { valueAsNumber: true })} />
          <p className="stamp text-mute mt-1.5">Held securely alongside both devices.</p>
        </div>
        <div className="col-span-2">
          <label className="label">Note for seller</label>
          <textarea className="input min-h-[100px]" placeholder="Anything they should know about your device…" {...register('notes')} />
        </div>

        <button disabled={isSubmitting} className="btn btn-lg btn-primary col-span-2 mt-2">
          {isSubmitting ? 'Sending…' : 'Send swap proposal →'}
        </button>
      </form>
    </div>
  );
}
